import { useEffect, useRef, useState } from "react";
import { Bot, X, Send, Loader2, ClipboardList, PhoneCall } from "lucide-react";
import { API, AZIENDA } from "@/lib/config";
import { useConsenso, salvaConsenso } from "@/lib/consenso";

const getSession = () => {
  let s = localStorage.getItem("idro_chat_session");
  if (!s) { s = `s-${crypto.randomUUID()}`; localStorage.setItem("idro_chat_session", s); }
  return s;
};

const BENVENUTO = { role: "assistant", content: "Ciao! Sono l'assistente virtuale. Descrivimi il problema idraulico (es. \"perde acqua sotto il lavello\") e ti dico cosa fare subito e quanto è urgente." };

async function streamChat(sessionId, message, onDelta) {
  const res = await fetch(`${API}/assistente/chat`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, message, consenso: true }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    onDelta(typeof j.detail === "string" ? j.detail : "Assistente non disponibile. Chiamaci o scrivici su WhatsApp.");
    return;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const data = p.replace(/^data: /, "");
      if (data === "[DONE]") return;
      const j = JSON.parse(data);
      const testo = j.delta ?? j.error;
      if (testo) onDelta(j.delta ? testo : `\n${testo}`);
    }
  }
}

export const AssistenteChat = ({ open, setOpen, onUsaNelModulo }) => {
  const [msgs, setMsgs] = useState([BENVENUTO]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const session = useRef(getSession());
  const consenso = useConsenso();
  const abilitato = !!consenso?.terze_parti;

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const invia = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setLoading(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      await streamChat(session.current, text, (d) =>
        setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d }; return c; }));
    } catch {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Assistente non disponibile. Chiamaci o scrivici su WhatsApp." }; return c; });
    } finally { setLoading(false); }
  };

  const testoUtente = msgs.filter((m) => m.role === "user").map((m) => m.content).join("\n");

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} aria-label="Apri assistente virtuale" className="fixed z-50 right-3 bottom-[5.5rem] md:right-4 md:bottom-6 flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white w-14 md:w-auto md:pl-4 md:pr-5 h-14 shadow-xl hover:bg-slate-800 transition-colors" data-testid="chat-open-btn">
          <Bot className="w-6 h-6 text-sky-300" /> <span className="hidden md:inline font-bold">Assistente</span>
        </button>
      )}
      {open && (
        <div className="fixed z-50 inset-x-2 bottom-2 md:inset-x-auto md:right-6 md:bottom-6 md:w-[400px] h-[85dvh] md:h-[600px] flex flex-col rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden" data-testid="chat-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
            <div className="flex items-center gap-2"><Bot className="w-5 h-5 text-sky-300" /><div><p className="font-bold leading-tight">Assistente virtuale</p><p className="text-xs text-slate-300">Risposte automatiche · AI</p></div></div>
            <button onClick={() => setOpen(false)} className="w-9 h-9 grid place-items-center rounded-full hover:bg-white/10" aria-label="Chiudi" data-testid="chat-close-btn"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="chat-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-sky-600 text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`} data-testid={`chat-message-${m.role}`}>
                {m.content || <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="px-3 pt-2 flex gap-2 border-t border-slate-200">
            <a href={AZIENDA.telefonoLink} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-600 text-white text-xs font-bold h-9" data-testid="chat-call-btn"><PhoneCall className="w-4 h-4" /> Chiama H24</a>
            <button disabled={!testoUtente} onClick={() => onUsaNelModulo(testoUtente)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-sky-50 text-sky-800 border border-sky-200 text-xs font-bold h-9 disabled:opacity-50" data-testid="chat-use-form-btn"><ClipboardList className="w-4 h-4" /> Richiedi intervento</button>
          </div>
          {!abilitato && (
            <div className="p-3 space-y-2 text-xs text-slate-600" data-testid="chat-consenso-richiesto">
              <p>L'assistente invia i tuoi messaggi a un servizio di intelligenza artificiale di terze parti. Per usarlo serve il tuo consenso (<a href="/privacy" className="underline text-sky-700">informativa</a>).</p>
              <button onClick={() => salvaConsenso({ terze_parti: true })} className="w-full h-10 rounded-xl bg-sky-600 text-white text-sm font-bold hover:bg-sky-700" data-testid="chat-consenso-btn">Acconsento e attiva l'assistente</button>
            </div>
          )}
          <form onSubmit={invia} className={`p-3 flex gap-2 ${abilitato ? "" : "hidden"}`}>
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Scrivi il tuo problema..." className="flex-1 h-11 rounded-xl border border-slate-300 px-3 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-sky-300" data-testid="chat-input" />
            <button disabled={loading || !input.trim()} className="w-11 h-11 grid place-items-center rounded-xl bg-sky-600 text-white disabled:opacity-50" aria-label="Invia" data-testid="chat-send-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};
